import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Linkedin, Instagram } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { useSite } from "@/lib/site";

// Minimal X (Twitter) glyph — Lucide does not ship a filled X mark that reads
// well at small sizes, so we render an inline path.
const XIcon = ({ className = "w-4 h-4" }) => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.65l-5.212-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
);

export default function Footer() {
    const [year, setYear] = useState("2026");
    const site = useSite();

    useEffect(() => {
        setYear(String(new Date().getFullYear()));
    }, []);

    return (
        <footer
            data-testid={FOOTER.section}
            className="bg-[#1B1B1B] text-[#F6F3EC] pt-24 md:pt-32 overflow-hidden relative"
        >
            <div className="mx-auto max-w-[1800px] px-6 md:px-10 lg:px-16">
                {/* Top block */}
                <div className="grid grid-cols-12 gap-6 md:gap-8 pb-24 md:pb-32">
                    <div className="col-span-12 md:col-span-4">
                        <p className="eyebrow text-[#F6F3EC]/60 mb-6">
                            Depmont · Est. 2026
                        </p>
                        <p className="font-serif-editorial italic text-3xl md:text-4xl leading-tight text-[#F6F3EC] max-w-sm">
                            An independent creative company, quietly building
                            what deserves to last.
                        </p>

                        {/* Socials */}
                        <div className="mt-10">
                            <p className="eyebrow text-[#F6F3EC]/60 mb-4">
                                Follow
                            </p>
                            <div className="flex items-center gap-3">
                                <a
                                    href={site.linkedin_url || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={FOOTER.socialLinkedin}
                                    aria-label="LinkedIn"
                                    className="w-10 h-10 inline-flex items-center justify-center border border-[#F6F3EC]/25 text-[#F6F3EC] transition-colors duration-500 hover:border-[#A8844F] hover:text-[#A8844F]"
                                >
                                    <Linkedin className="w-[18px] h-[18px]" strokeWidth={1.4} />
                                </a>
                                <a
                                    href={site.instagram_url || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={FOOTER.socialInstagram}
                                    aria-label="Instagram"
                                    className="w-10 h-10 inline-flex items-center justify-center border border-[#F6F3EC]/25 text-[#F6F3EC] transition-colors duration-500 hover:border-[#A8844F] hover:text-[#A8844F]"
                                >
                                    <Instagram className="w-[18px] h-[18px]" strokeWidth={1.4} />
                                </a>
                                <a
                                    href={site.x_url || "#"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={FOOTER.socialX}
                                    aria-label="X"
                                    className="w-10 h-10 inline-flex items-center justify-center border border-[#F6F3EC]/25 text-[#F6F3EC] transition-colors duration-500 hover:border-[#A8844F] hover:text-[#A8844F]"
                                >
                                    <XIcon className="w-[15px] h-[15px]" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="col-span-6 md:col-span-2 md:col-start-6">
                        <p className="eyebrow text-[#F6F3EC]/60 mb-6">
                            Navigate
                        </p>
                        <ul className="space-y-3 font-sans-tight text-sm">
                            <li>
                                <Link to="/" className="text-[#F6F3EC] link-underline">
                                    Home
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/#work"
                                    className="text-[#F6F3EC] link-underline"
                                >
                                    Work
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/approach"
                                    className="text-[#F6F3EC] link-underline"
                                >
                                    Approach
                                </Link>
                            </li>
                            <li>
                                <Link to="/about" className="text-[#F6F3EC] link-underline">
                                    About
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/careers"
                                    className="text-[#F6F3EC] link-underline"
                                >
                                    Careers
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/contact"
                                    className="text-[#F6F3EC] link-underline"
                                >
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="col-span-6 md:col-span-2">
                        <p className="eyebrow text-[#F6F3EC]/60 mb-6">
                            Practice
                        </p>
                        <ul className="space-y-3 font-sans-tight text-sm">
                            <li>Graphic Design</li>
                            <li>YouTube Design</li>
                            <li>Social &amp; Editorial</li>
                            <li>Marketing Campaigns</li>
                            <li>Community Systems</li>
                            <li>Brand Systems</li>
                        </ul>
                    </div>

                    <div className="col-span-12 md:col-span-3 md:col-start-10">
                        <p className="eyebrow text-[#F6F3EC]/60 mb-6">
                            Correspondence
                        </p>
                        <a
                            href="mailto:contact@depmont.com"
                            className="font-serif-editorial text-2xl md:text-3xl link-underline text-[#F6F3EC]"
                        >
                            contact@depmont.com
                        </a>
                        <p className="mt-6 text-sm text-[#F6F3EC]/60 font-sans-tight leading-relaxed max-w-xs font-light">
                            Independent Creative Company. Selective
                            engagements, worldwide.
                        </p>
                    </div>
                </div>
            </div>

            {/* THE BOLD MOVE — giant wordmark */}
            <div className="w-full overflow-hidden pointer-events-none select-none">
                <motion.div
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full"
                >
                    <h2
                        data-testid={FOOTER.wordmark}
                        aria-label="Depmont"
                        className="font-serif-editorial text-[#F6F3EC] leading-[0.85] tracking-[-0.045em] text-center"
                        style={{ fontSize: "clamp(6rem, 25.5vw, 30rem)" }}
                    >
                        Depmont<span className="text-[#A8844F]">.</span>
                    </h2>
                </motion.div>
            </div>

            <div className="border-t border-[#F6F3EC]/15">
                <div className="mx-auto max-w-[1800px] px-6 md:px-10 lg:px-16 py-8 flex flex-col md:flex-row justify-between gap-4 text-[11px] font-sans-tight tracking-[0.18em] uppercase text-[#F6F3EC]/70">
                    <p>
                        © {year} Depmont. All rights reserved.
                    </p>
                    <div className="flex gap-8">
                        <Link
                            to="/privacy"
                            data-testid={FOOTER.linkPrivacy}
                            className="link-underline text-[#F6F3EC]/70"
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            to="/terms"
                            data-testid={FOOTER.linkTerms}
                            className="link-underline text-[#F6F3EC]/70"
                        >
                            Terms of Service
                        </Link>
                    </div>
                    <p>Built to endure.</p>
                </div>
            </div>
        </footer>
    );
}
